import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  OnInit,
  PLATFORM_ID,
  signal,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { select, Store } from '@ngxs/store';
import {
  AnimateModule,
  InvalidControlScrollDirective,
  ValidateMessageComponent,
  WwwAppState,
  WwwRouteSlug,
} from '@pro-connect/shared';
import { MyDataState, MyDataStateChangeClientAgreement } from '../../my-data.state';
import { combineLatest, debounceTime, distinctUntilChanged, skip, tap } from 'rxjs';

export type AgreementsFormType = {
  agreementSms: FormControl<boolean | null>;
  agreementEmail: FormControl<boolean | null>;
  agreementPhone: FormControl<boolean | null>;
  agreementWhatsApp: FormControl<boolean | null>;
  agreement: FormControl<boolean | null>;
};

@Component({
  selector: 'app-agreements',
  templateUrl: './agreements.component.html',
  styleUrls: ['./agreements.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    AnimateModule,
    ReactiveFormsModule,
    FormsModule,
    NgxsFormPluginModule,
    ValidateMessageComponent,
    InvalidControlScrollDirective,
  ],
})
export class AgreementsComponent implements OnInit {
  protected readonly _store: Store = inject(Store);

  public readonly loading = select(MyDataState.loading);
  public readonly user = select(WwwAppState.user);
  public readonly WwwRouteSlug = WwwRouteSlug;
  public readonly formGroup: FormGroup<AgreementsFormType>;

  public readonly extendedRules: WritableSignal<boolean> = signal(false);

  protected readonly _platformId = inject(PLATFORM_ID);
  protected readonly _isBrowser = isPlatformBrowser(this._platformId);

  constructor(private readonly _formBuilder: FormBuilder) {
    this.formGroup = this._formBuilder.group({
      agreementSms: new FormControl<boolean>(false, [Validators.required]),
      agreementEmail: new FormControl<boolean>(false, [Validators.required]),
      agreementPhone: new FormControl<boolean>(false, [Validators.required]),
      agreementWhatsApp: new FormControl<boolean>(false, [Validators.required]),
      agreement: new FormControl<boolean>({ value: true, disabled: true }, [Validators.required]),
    });
    if (this.user()) {
      effect(() => {
        const me = this.user();
        this.formGroup.patchValue(
          {
            agreementSms: me?.agreement_sms,
            agreementEmail: me?.agreement_email,
            agreementPhone: me?.agreement_phone,
            agreementWhatsApp: me?.agreement_whats_app,
          },
          { emitEvent: false },
        );
      });
    }
  }

  get f() {
    return this.formGroup.controls;
  }

  public ngOnInit(): void {
    this._updateAgreements();
  }

  private _updateAgreements(): void {
    combineLatest([
      this.f.agreementEmail.valueChanges,
      this.f.agreementPhone.valueChanges,
      this.f.agreementSms.valueChanges,
      this.f.agreementWhatsApp.valueChanges,
    ])
      .pipe(
        skip(1),
        debounceTime(500),
        distinctUntilChanged(),
        tap((_) => {
          this._store.dispatch([
            new MyDataStateChangeClientAgreement({
              agreementEmail: this.f.agreementEmail.value ? 1 : 0,
              agreementPhone: this.f.agreementPhone.value ? 1 : 0,
              agreementSms: this.f.agreementSms.value ? 1 : 0,
              agreementWhatsApp: this.f.agreementWhatsApp.value ? 1 : 0,
            }),
          ]);
        }),
      )
      .subscribe();
  }
}
